/**
 * Build destination unique chunk/match for new references: chunk is the smallest
 * substring of the document that contains the selection, such that (1) the chunk
 * appears exactly once in the full document text, and (2) the match appears exactly
 * once inside the chunk.
 */

/** Count possibly-overlapping occurrences of `needle` in `haystack`. */
export function countSubstringOccurrences(
  haystack: string,
  needle: string
): number {
  if (needle.length === 0) {
    return 0;
  }
  let count = 0;
  let pos = 0;
  while (pos <= haystack.length - needle.length) {
    const i = haystack.indexOf(needle, pos);
    if (i === -1) {
      break;
    }
    count++;
    pos = i + 1;
  }
  return count;
}

const normalizeNbsp = (s: string): string => s.replace(/\u00a0/g, " ");

/**
 * Tags chrome a viewer feature inserts under `.doc-root` itself, such as the
 * diff navigation's "Vis begge versioner" button and foldout. Its text is not
 * document text, so the visible-text snapshot skips it and search never hits
 * it (ADR 0001).
 */
export const VIEWER_CHROME_ATTRIBUTE = "data-viewer-chrome";

/**
 * Nodes whose text is not part of the clean consolidated document:
 * amendment-diff deletions (`del`), re-inserted deleted "ghost" containers and
 * tagged viewer chrome. Their text must be excluded from reference match/chunk
 * computation so a reference stays resolvable against the clean document
 * regardless of the "Fremhæv ændringer" toggle state.
 */
const DECORATION_SELECTOR = `del, [data-consolidation-deleted="true"], [${VIEWER_CHROME_ATTRIBUTE}]`;

/**
 * True when `node` is, or is contained by, a decoration element (bounded by
 * `root`). Accepts either an element or a text node.
 */
export function isWithinDecoration(node: Node, root: Element): boolean {
  let current: Node | null =
    node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode;
  while (current && current !== root) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      (current as Element).matches(DECORATION_SELECTOR)
    ) {
      return true;
    }
    current = current.parentNode;
  }
  return false;
}

/**
 * Per-walk memo of `isWithinDecoration` verdicts. Converted documents give every
 * element an id and the manual-sized ones have tens of thousands of nodes; the
 * plain ancestor walk per text node made one selection cost hundreds of ms
 * (LAR-252). Verdicts are cached per element and resolved via the parent's
 * cached verdict, so a whole walk costs O(elements) instead of O(nodes x depth).
 */
function createDecorationVerdicts(root: Element): (el: Element | null) => boolean {
  const verdicts = new WeakMap<Element, boolean>();
  const isDecorated = (el: Element | null): boolean => {
    if (!el || el === root) return false;
    const cached = verdicts.get(el);
    if (cached !== undefined) return cached;
    const verdict =
      el.matches(DECORATION_SELECTOR) || isDecorated(el.parentElement);
    verdicts.set(el, verdict);
    return verdict;
  };
  return isDecorated;
}

/** Ordered list of text nodes under `root` that are not inside a decoration. */
function collectVisibleTextNodes(root: HTMLElement): Text[] {
  const doc = root.ownerDocument;
  if (!doc) {
    return [];
  }
  const isDecorated = createDecorationVerdicts(root);
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      isDecorated(node.parentElement)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  const nodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    nodes.push(current as Text);
    current = walker.nextNode();
  }
  return nodes;
}

/**
 * One walk's worth of visible-text data: the nodes in document order, the
 * concatenated clean text, and per-node prefix offsets into that text
 * (`prefix[i]` = characters before `nodes[i]`). Lets a caller that needs both
 * the text and range offsets pay for the DOM walk once.
 */
export interface VisibleTextSnapshot {
  nodes: Text[];
  prefix: number[];
  fullText: string;
}

export function getVisibleTextSnapshot(root: HTMLElement): VisibleTextSnapshot {
  const nodes = collectVisibleTextNodes(root);
  const prefix: number[] = new Array(nodes.length);
  let total = 0;
  const parts: string[] = new Array(nodes.length);
  for (let i = 0; i < nodes.length; i++) {
    prefix[i] = total;
    parts[i] = nodes[i].data;
    total += nodes[i].data.length;
  }
  return { nodes, prefix, fullText: parts.join("") };
}

/**
 * The clean consolidated document text: `root`'s text with all decoration text
 * (deletions, deleted ghosts) removed. This is the string space that reference
 * match/chunk offsets are computed in.
 */
export function getVisibleDocText(root: HTMLElement): string {
  return getVisibleTextSnapshot(root).fullText;
}

/**
 * Number of visible characters before the DOM point (container, offset). A point
 * that falls inside deleted text is clamped to the nearest visible boundary
 * (the deleted portion contributes nothing).
 */
function visibleOffsetForPoint(
  root: HTMLElement,
  snapshot: VisibleTextSnapshot,
  container: Node,
  offset: number
): number | null {
  const doc = root.ownerDocument;
  if (!doc) {
    return null;
  }
  let pointRange: Range;
  try {
    pointRange = doc.createRange();
    pointRange.setStart(root, 0);
    pointRange.setEnd(container, offset);
  } catch {
    return null;
  }

  const { nodes, prefix, fullText } = snapshot;

  // Nodes are in document order, so "node ends at or before the point" is
  // monotonic. Binary-search the first node that ends after the point instead
  // of comparing every node — manual-sized documents have thousands.
  const endsBeforePoint = (index: number): boolean => {
    try {
      return (
        pointRange.comparePoint(nodes[index], nodes[index].data.length) <= 0
      );
    } catch {
      // Not comparable (detached/foreign node): order-wise treat it like the
      // sequential scan did, which skipped it and kept counting.
      return true;
    }
  };

  let lo = 0;
  let hi = nodes.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (endsBeforePoint(mid)) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  if (lo >= nodes.length) {
    return fullText.length;
  }

  const node = nodes[lo];
  let startCompare: number;
  try {
    startCompare = pointRange.comparePoint(node, 0);
  } catch {
    return prefix[lo];
  }
  if (startCompare > 0) {
    // This node begins after the point.
    return prefix[lo];
  }
  // The point falls inside this node (container === node, offset within it).
  return prefix[lo] + Math.min(Math.max(offset, 0), node.data.length);
}

/**
 * Range start/end offsets in the clean-text space of `snapshot` (see
 * `getVisibleTextSnapshot`). Boundaries inside deleted text are clamped to the
 * nearest visible position.
 */
export function getVisibleRangeTextOffsetsFromSnapshot(
  root: HTMLElement,
  snapshot: VisibleTextSnapshot,
  range: Range
): { start: number; end: number } | null {
  const start = visibleOffsetForPoint(
    root,
    snapshot,
    range.startContainer,
    range.startOffset
  );
  const end = visibleOffsetForPoint(
    root,
    snapshot,
    range.endContainer,
    range.endOffset
  );
  if (start === null || end === null || start > end || start < 0) {
    return null;
  }
  return { start, end };
}

/**
 * Range start/end offsets in the clean-text space (see `getVisibleDocText`).
 * Boundaries inside deleted text are clamped to the nearest visible position.
 */
export function getVisibleRangeTextOffsets(
  root: HTMLElement,
  range: Range
): { start: number; end: number } | null {
  return getVisibleRangeTextOffsetsFromSnapshot(
    root,
    getVisibleTextSnapshot(root),
    range
  );
}

function isValidChunk(
  fullText: string,
  chunkStart: number,
  chunkEnd: number,
  selStart: number,
  selEnd: number
): boolean {
  if (chunkStart > selStart || chunkEnd < selEnd) {
    return false;
  }
  const chunk = fullText.slice(chunkStart, chunkEnd);
  const match = fullText.slice(selStart, selEnd);
  if (!match) {
    return false;
  }
  if (countSubstringOccurrences(chunk, match) !== 1) {
    return false;
  }
  if (countSubstringOccurrences(fullText, chunk) !== 1) {
    return false;
  }
  return true;
}

/**
 * Expand from the selection, then shrink from both sides while constraints hold.
 * Uses the same string space as `root.textContent`.
 */
export function computeMinimalUniqueChunk(
  fullText: string,
  selStart: number,
  selEnd: number
): string {
  if (selStart >= selEnd || selStart < 0 || selEnd > fullText.length) {
    return fullText.slice(selStart, selEnd);
  }

  let cs = selStart;
  let ce = selEnd;
  let preferLeft = true;

  while (!isValidChunk(fullText, cs, ce, selStart, selEnd)) {
    const canLeft = cs > 0;
    const canRight = ce < fullText.length;
    if (!canLeft && !canRight) {
      return fullText;
    }
    if (canLeft && canRight) {
      if (preferLeft) {
        cs--;
      } else {
        ce++;
      }
      preferLeft = !preferLeft;
    } else if (canLeft) {
      cs--;
    } else {
      ce++;
    }
  }

  while (cs < selStart && isValidChunk(fullText, cs + 1, ce, selStart, selEnd)) {
    cs++;
  }
  while (ce > selEnd && isValidChunk(fullText, cs, ce - 1, selStart, selEnd)) {
    ce--;
  }

  return fullText.slice(cs, ce);
}

/**
 * Apply NBSP normalization for storage/highlight parity; keeps chunk ⊇ match as substring.
 */
export function normalizeChunkAndMatchForStorage(
  chunkText: string,
  matchText: string
): { chunkText: string; matchText: string } {
  const chunkNorm = normalizeNbsp(chunkText);
  const matchNorm = normalizeNbsp(matchText);
  if (chunkNorm.includes(matchNorm)) {
    return { chunkText: chunkNorm, matchText: matchNorm };
  }
  return { chunkText: chunkText, matchText: matchText };
}
